import { toDateValue } from './relativeTime';

/**
 * Logique PURE du sous-onglet « Incidents serveur » (lot I5) — aucune dépendance Firebase ni React :
 * tout est vérifiable par `react-scripts test` sans émulateur (même doctrine que
 * `utils/mailDelivery.js`, le module pur du lot I2a).
 *
 * SOURCE : la collection `serverMonitoring` du projet `msd-portail`, écrite par le SERVEUR
 * (`deployment-service/src/services/server-monitoring.js` et son jumeau portail) — JAMAIS par ce
 * panneau. Le vocabulaire ci-dessous est CELUI DU SERVEUR (constat C8 : la source, l'état et la
 * gravité sont des valeurs FERMÉES) ; un écran qui inventerait ses propres libellés mentirait.
 *
 * Ce que ce module ne fait PAS : il ne lit rien (`serverMonitoringService` lit) et n'écrit rien
 * (ce lot est en LECTURE SEULE, DI5-9 / DI5-12). Normalisation, tri, filtres et libellés vivent
 * ICI, donc sont testables hors ligne.
 *
 * Décisions appliquées : DI5-8 (colonnes + filtres en mémoire), DI5-9 (aucune mutation),
 * DI5-11 (preuve par bancs), DI5-12 (aucun index).
 */

/**
 * Nombre maximum d'incidents lus (modèle `MAILS_LIMIT` du lot I2a). La collection est un canal
 * d'ESCALADE : elle compte peu de documents (constat C8) ⇒ cette borne ne tronque rien aujourd'hui.
 */
export const INCIDENTS_LIMIT = 200;

/** Sources d'incident — vocabulaire FERMÉ du serveur (constat C8). */
export const SOURCES_INCIDENT = [
  { id: 'tous', label: 'Toutes les sources' },
  { id: 'registration', label: 'Enregistrement' },
  { id: 'dns', label: 'DNS' },
  { id: 'certificate', label: 'Certificat' },
  { id: 'publication', label: 'Publication' },
  { id: 'cleanup', label: 'Nettoyage' }
];

/** États de suivi support (`incident.status`). */
export const STATUTS_INCIDENT = [
  { id: 'tous', label: 'Tous les états' },
  { id: 'open', label: 'Ouvert' },
  { id: 'acknowledged', label: 'Pris en compte' },
  { id: 'resolved', label: 'Résolu' }
];

/** Gravités (`incident.severity`) : « À traiter » vs « Trace » (décision D3c-22 du cadrage 3c). */
export const SEVERITES_INCIDENT = [
  { id: 'tous', label: 'Toutes les gravités' },
  { id: 'action-required', label: 'À traiter' },
  { id: 'info', label: 'Trace (aucune action)' }
];

const CLASSE_INCIDENT = 'bg-error/20 text-error border border-error/30';
const CLASSE_TRACE = 'bg-base-300 text-base-content border border-base-300';
const CLASSE_OUVERT = 'bg-warning/20 text-warning border border-warning/30';
const CLASSE_PRIS_EN_COMPTE = 'bg-info/20 text-info border border-info/30';
const CLASSE_RESOLU = 'bg-success/20 text-success border border-success/30';

const LIBELLES_SOURCE = {
  registration: 'Enregistrement',
  dns: 'DNS',
  certificate: 'Certificat',
  publication: 'Publication',
  cleanup: 'Nettoyage'
};

const LIBELLES_STATUT = {
  open: 'Ouvert',
  acknowledged: 'Pris en compte',
  resolved: 'Résolu'
};

const LIBELLES_GRAVITE = {
  'action-required': 'À traiter',
  info: 'Trace (aucune action)'
};

/** Nettoie une valeur en texte exploitable (chaîne vide si inutilisable). */
const texte = (valeur) => {
  if (typeof valeur === 'string') return valeur.trim();
  if (typeof valeur === 'number' && Number.isFinite(valeur)) return String(valeur);
  return '';
};

/**
 * Rend une valeur prête à AFFICHER : jamais `undefined` ni vide à l'écran (banc B10).
 * @param {*} valeur - Valeur brute du document
 * @returns {string} Texte, ou « — » si la valeur est absente
 */
export const valeurAffichee = (valeur) => {
  if (valeur === null || valeur === undefined) return '—';
  const rendu = String(valeur).trim();
  return rendu === '' ? '—' : rendu;
};

// Libellés : on TRADUIT une valeur connue, on n'INVENTE pas un libellé pour une valeur inconnue
// (doctrine des écrans existants) — une valeur hors vocabulaire s'affiche telle quelle.
export const libelleSource = (source) => LIBELLES_SOURCE[texte(source)] || valeurAffichee(source);
export const libelleStatut = (statut) => LIBELLES_STATUT[texte(statut)] || valeurAffichee(statut);
export const libelleGravite = (gravite) => LIBELLES_GRAVITE[texte(gravite)] || valeurAffichee(gravite);

/** Classe CSS (daisyUI) d'une pastille d'état de suivi. */
export const classeStatut = (statut) => {
  const valeur = texte(statut);
  if (valeur === 'acknowledged') return CLASSE_PRIS_EN_COMPTE;
  if (valeur === 'resolved') return CLASSE_RESOLU;
  return CLASSE_OUVERT;
};

/**
 * Nature d'un document (décision D3c-22, révélée par `severity` / `business`) : un INCIDENT à
 * traiter (panne interne) n'est pas une TRACE informative (refus métier, sans action à mener).
 * @param {Object} incident - Incident normalisé
 * @returns {{id: string, label: string, classe: string}}
 */
export const natureIncident = (incident) => {
  const business = !!(incident && incident.business === true);
  const gravite = texte(incident && incident.severity);
  if (business || gravite === 'info') {
    return { id: 'trace', label: 'Trace (aucune action)', classe: CLASSE_TRACE };
  }
  return { id: 'incident', label: 'Incident (à traiter)', classe: CLASSE_INCIDENT };
};

/**
 * Normalise un document `serverMonitoring` pour l'écran.
 *
 * Champs absents ⇒ `null` (rendus « — » par `valeurAffichee`, jamais « undefined » : banc B10).
 * Deux replis de BON SENS alignés sur le serveur : `status` manquant = `open` (état initial écrit
 * par le serveur) et `severity` manquante = déduite de `business` (même règle que le serveur).
 * @param {string} id - Identifiant du document
 * @param {Object} [data] - Données brutes du document
 * @returns {Object} Incident prêt à afficher
 */
export const normaliserIncident = (id, data = {}) => {
  const brut = data && typeof data === 'object' ? data : {};
  const business = brut.business === true;
  const gravite = texte(brut.severity) || (business ? 'info' : 'action-required');

  return {
    id: texte(id),
    userId: texte(brut.userId) || null,
    domainName: texte(brut.domainName) || null,
    trackingId: texte(brut.trackingId) || null,
    source: texte(brut.source) || null,
    errorCode: texte(brut.errorCode) || null,
    errorMessage: texte(brut.errorMessage) || null,
    attempts: typeof brut.attempts === 'number' && Number.isFinite(brut.attempts) ? brut.attempts : null,
    lastVerdict: texte(brut.lastVerdict) || null,
    business,
    severity: gravite,
    status: texte(brut.status) || 'open',
    occurrences: typeof brut.occurrences === 'number' && brut.occurrences >= 1
      ? Math.floor(brut.occurrences)
      : 1,
    firstOccurredAt: toDateValue(brut.firstOccurredAt),
    lastOccurredAt: toDateValue(brut.lastOccurredAt)
  };
};

/**
 * Trie les incidents du plus RÉCENT au plus ancien, SANS `orderBy` Firestore (donc sans index —
 * DI5-8). Les documents sans date exploitable sont classés EN FIN ; à date égale, l'ordre
 * d'origine est conservé (tri stable).
 * @param {Array<Object>} liste - Incidents normalisés
 * @returns {Array<Object>} Nouveau tableau trié
 */
export const trierIncidents = (liste) => {
  const source = Array.isArray(liste) ? liste : [];
  return source
    .map((incident, index) => {
      const date = toDateValue(incident && incident.lastOccurredAt) ||
        toDateValue(incident && incident.firstOccurredAt);
      return { incident, index, ms: date ? date.getTime() : null };
    })
    .sort((a, b) => {
      if (a.ms === null && b.ms === null) return a.index - b.index;
      if (a.ms === null) return 1;
      if (b.ms === null) return -1;
      if (a.ms !== b.ms) return b.ms - a.ms;
      return a.index - b.index;
    })
    .map((entree) => entree.incident);
};

/**
 * Applique les filtres d'écran EN MÉMOIRE (DI5-8) : `source`, `status`, `severity` se combinENT en
 * INTERSECTION (banc B11). `'tous'` laisse passer.
 * @param {Array<Object>} liste - Incidents normalisés
 * @param {Object} [filtres] - `{source, status, severity}`
 * @returns {Array<Object>} Incidents retenus
 */
export const filtrerIncidents = (liste, { source = 'tous', status = 'tous', severity = 'tous' } = {}) => {
  const documents = Array.isArray(liste) ? liste : [];
  return documents.filter((incident) => {
    if (!incident) return false;
    if (source !== 'tous' && incident.source !== source) return false;
    if (status !== 'tous' && incident.status !== status) return false;
    if (severity !== 'tous' && incident.severity !== severity) return false;
    return true;
  });
};

/**
 * Compteurs de tête du sous-onglet : total, incidents à traiter, traces, et ouverts.
 * @param {Array<Object>} liste - Incidents normalisés
 * @returns {{total: number, aTraiter: number, traces: number, ouverts: number}}
 */
export const compterIncidents = (liste) => {
  const documents = Array.isArray(liste) ? liste : [];
  let aTraiter = 0;
  let traces = 0;
  let ouverts = 0;
  for (const incident of documents) {
    if (natureIncident(incident).id === 'trace') traces += 1;
    else aTraiter += 1;
    if (incident && incident.status === 'open') ouverts += 1;
  }
  return { total: documents.length, aTraiter, traces, ouverts };
};
